import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers';
import PlaceIcon from '@material-ui/icons/Place';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import UploadFiles from '../../Common/UploadFiles';
import CommentsTable from '../../Common/CommentsTable';
import { ModalDefaultDraggable } from '../../../../../molecules';
import { RootState } from '../../../../../redux/root';
import schema from './schema';
import { setBottomDrawerOpened } from '../../../../../redux/slices/generalSlice';
import { updatePermitActivity } from '../../../../../api/activities';
import { updatePermitConditionItem } from '../../../../../redux/slices/conditionsSlice';
import { onConditionSelectedWithDispatch } from './ConditionsTable';

const ConditionsControls = () => {
  type FormValue = {
    notes: string;
    documentFile: any[];
  };
  const comments = useSelector((s: RootState) => s.conditions.Comments);
  const bottomDrawerOpened = useSelector((s: RootState) => s.general.bottomDrawerOpened);
  const condition = useSelector((s: RootState) => s.conditions.SelectedCondition);
  const conditionName = useSelector((s: RootState) => s.conditions.ConditionName);
  const selectedConditionItem = useSelector((s: RootState) => s.conditions.selectedConditionItem);
  const fullActivity = useSelector((s: RootState) =>
    s.conditions.PermitConditions.find(p => p.id === selectedConditionItem?.id),
  );
  const { handleSubmit, register, errors, setValue } = useForm<FormValue>({
    resolver: yupResolver(schema),
    defaultValues: {} as FormValue,
  });
  const dispatch = useDispatch();

  const onClose = () => {
    dispatch(setBottomDrawerOpened(false));
  };

  const onSubmit = async form => {
    const data: any = {
      id: selectedConditionItem.id,
      permit: fullActivity?.permit,
      activityType: (fullActivity as any)?.activityType,
      title: selectedConditionItem.title,
      status: selectedConditionItem.status,
      information: form.notes,
      documentFile: form.documentFile,
    };
    try {
      const response: any = await updatePermitActivity(data);
      dispatch(
        updatePermitConditionItem({
          id: selectedConditionItem.id,
          ...(response?.data || {}),
          information: form.notes,
        }),
      );
      await onConditionSelectedWithDispatch(condition, dispatch);
      onClose();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!bottomDrawerOpened) return;
    setTimeout(() => {
      setValue('notes', fullActivity?.information || '');
    }, 10);
  }, [bottomDrawerOpened, fullActivity]);

  if (!bottomDrawerOpened) return null;

  const attachments = selectedConditionItem?.attachments || [];
  // @ts-ignore
  const address = condition?.address || '';

  return (
    <ModalDefaultDraggable title="Edit Condition" onClose={onClose} wide addContactModal zIndex={20}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormBody>
          <HeaderCard>
            <HeaderCol>
              <HeaderLabel>Condition / Permit</HeaderLabel>
              <HeaderTitle title={conditionName || ''}>{conditionName || '—'}</HeaderTitle>
            </HeaderCol>
            <HeaderDivider />
            <HeaderCol>
              <HeaderLabel>Address</HeaderLabel>
              <HeaderAddress title={address}>
                <PlaceIcon style={{ fontSize: 16 }} />
                <span>{address || '—'}</span>
              </HeaderAddress>
            </HeaderCol>
          </HeaderCard>

          <MetaRow>
            <MetaItem>
              <MetaLabel>Status</MetaLabel>
              <MetaValue>{selectedConditionItem?.statusName || '—'}</MetaValue>
            </MetaItem>
          </MetaRow>

          {attachments.length > 0 && (
            <Group>
              <GroupLabel>Attachments</GroupLabel>
              <Attachments>
                {attachments.map((attach, index) => (
                  <Attachment key={index}>
                    <DownloadLink href={attach.file} target="_blank" rel="noreferrer" title={attach.visibleFilename}>
                      <CloudDownloadIcon style={{ fontSize: 18 }} />
                      <span>{attach.visibleFilename}</span>
                    </DownloadLink>
                  </Attachment>
                ))}
              </Attachments>
            </Group>
          )}

          <Group>
            <GroupLabel>Comments</GroupLabel>
            {comments.length === 0 ? <EmptyLine>No comments yet.</EmptyLine> : <CommentsTable comments={comments} />}
          </Group>

          <Group>
            <GroupLabel>Notes</GroupLabel>
            <Notes
              name="notes"
              rows={5}
              ref={register}
              defaultValue={fullActivity?.information || ''}
              $error={errors.notes !== undefined}
              placeholder="Add any notes for this condition…"
            />
            {errors.notes !== undefined && <ErrorText>{errors.notes.message}</ErrorText>}
          </Group>

          <Group>
            <GroupLabel>Attach files</GroupLabel>
            <UploadFiles setValue={setValue} register={register} />
          </Group>
        </FormBody>

        <Footer>
          <FooterNote>Changes are saved when you click Update.</FooterNote>
          <FooterButtons>
            <CancelBtn type="button" onClick={onClose}>
              Cancel
            </CancelBtn>
            <UpdateBtn type="submit">Update</UpdateBtn>
          </FooterButtons>
        </Footer>
      </Form>
    </ModalDefaultDraggable>
  );
};

export default ConditionsControls;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 88px;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const MetaLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const MetaValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const EmptyLine = styled.div`
  padding: 8px 2px;
  color: var(--neutral-500);
  font-size: 0.8125rem;
`;

const Attachments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Attachment = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  min-width: 0;
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--primary-700);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  transition: color 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: var(--primary-600);
    text-decoration: underline;
  }
`;

const Notes = styled.textarea<{ $error: boolean }>`
  width: 100%;
  min-height: 120px;
  padding: 10px 12px;
  border: 1px solid ${({ $error }) => ($error ? 'var(--danger-500, #e0423f)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--mainText);
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const ErrorText = styled.div`
  color: var(--danger-500, #e0423f);
  font-size: 0.75rem;
`;

const Footer = styled.div`
  position: sticky;
  bottom: -35px;
  margin: 0 -35px -35px;
  padding: 14px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--neutral-0);
  border-top: 1px solid var(--border);
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.08);
  z-index: 5;
  flex-wrap: wrap;

  @media only screen and (max-width: 840px) {
    bottom: -22px;
    margin: 0 -22px -22px;
    padding: 12px 18px;
  }

  @media only screen and (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }

  @media only screen and (max-width: 540px) {
    bottom: -16px;
    margin: 0 -14px -16px;
    padding: 10px 14px;
  }
`;

const FooterNote = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media only screen and (max-width: 640px) {
    justify-content: flex-end;
  }
`;

const CancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const UpdateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
